import React, { useState, useEffect, useRef } from 'react';
import api, { UPLOADS_URL } from '../api';
import NotificationBanner from './NotificationBanner';

const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];
const CANADIAN_PROVINCES = ['Alberta','British Columbia','Manitoba','New Brunswick','Newfoundland and Labrador','Northwest Territories','Nova Scotia','Nunavut','Ontario','Prince Edward Island','Quebec','Saskatchewan','Yukon'];

function StateSelect({ value, onChange }) {
  return (
    <select value={value || ''} onChange={onChange}>
      <option value="">Select...</option>
      <optgroup label="United States">{US_STATES.map(s => <option key={s} value={s}>{s}</option>)}</optgroup>
      <optgroup label="Canada">{CANADIAN_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}</optgroup>
    </select>
  );
}

function AddressBlock({ prefix, data, onChange }) {
  const f = (n) => prefix ? `${prefix}_${n}` : n;
  return (
    <>
      <div className="form-group"><label>Street Address 1</label><input value={data[f('street1')] || ''} onChange={e => onChange(f('street1'), e.target.value)} placeholder="123 Main St" /></div>
      <div className="form-group"><label>Street Address 2 <span className="text-muted text-sm">(Optional)</span></label><input value={data[f('street2')] || ''} onChange={e => onChange(f('street2'), e.target.value)} placeholder="Apt, Suite, Unit..." /></div>
      <div className="field-grid-2">
        <div className="form-group"><label>City</label><input value={data[f('city')] || ''} onChange={e => onChange(f('city'), e.target.value)} /></div>
        <div className="form-group"><label>State / Province</label><StateSelect value={data[f('state')]} onChange={e => onChange(f('state'), e.target.value)} /></div>
      </div>
      <div className="field-grid-2">
        <div className="form-group"><label>ZIP / Postal Code</label><input value={data[f('zip')] || ''} onChange={e => onChange(f('zip'), e.target.value)} /></div>
        <div className="form-group"><label>Country</label>
          <select value={data[f('country')] || 'USA'} onChange={e => onChange(f('country'), e.target.value)}>
            <option value="USA">USA</option>
            <option value="Canada">Canada</option>
          </select>
        </div>
      </div>
    </>
  );
}

function PhotoManager({ reg, regType, maxPhotos, onUpdate }) {
  const [photos, setPhotos] = useState(reg.photos || []);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();
  const token = localStorage.getItem('token');
  const max = maxPhotos || 3;

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await api.delete(`/api/photos/${photoId}`, { headers: { Authorization: `Bearer ${token}` } });
      const updated = photos.filter(p => p.id !== photoId);
      setPhotos(updated);
      onUpdate(updated);
    } catch (err) { setError('Failed to delete photo.'); }
  };

  const handleSetPrimary = async (photoId) => {
    try {
      await api.put(`/api/photos/${photoId}/primary`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const photo = photos.find(p => p.id === photoId);
      const rest = photos.filter(p => p.id !== photoId);
      const updated = [photo, ...rest];
      setPhotos(updated);
      onUpdate(updated);
    } catch (err) { setError('Failed to set primary photo.'); }
  };

  const handleAdd = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (photos.length + files.length > max) {
      setError(`You have ${photos.length} photo(s). Maximum is ${max} total.`);
      return;
    }
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      for (const file of files) formData.append('photos', file);
      const res = await api.post(`/api/photos/${reg.id}/${regType}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos(res.data.photos);
      onUpdate(res.data.photos);
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed.');
    } finally {
      setUploading(false);
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="photo-manager">
      <div className="photo-manager-header">
        <span className="photo-manager-title">Photos ({photos.length}/{max})</span>
        {photos.length < max && (
          <button onClick={() => fileInputRef.current.click()} disabled={uploading}
            className="btn btn-success btn-sm">
            {uploading ? 'Uploading...' : '+ Add Photo'}
          </button>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" multiple className="visually-hidden" onChange={handleAdd} />
      </div>
      {error && <p className="error-text">{error}</p>}
      {photos.length === 0 ? (
        <p className="photo-manager-empty">No photos yet.</p>
      ) : (
        <div className="photo-manager-grid">
          {photos.map((photo, i) => (
            <div key={photo.id || i} className={`photo-thumb${i === 0 ? ' primary-photo' : ''}`}>
              <img src={`${UPLOADS_URL}/${photo.filename}`} alt={`Photo ${i + 1}`} />
              {i === 0 && <span className="photo-primary-badge">⭐ Primary</span>}
              <div className="photo-thumb-overlay">
                {i !== 0 && (
                  <button onClick={() => handleSetPrimary(photo.id)} className="photo-thumb-btn">⭐ Primary</button>
                )}
                <button onClick={() => handleDelete(photo.id)} className="photo-thumb-btn">✕ Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── EDIT FORMS ─────────────────────────────────────────────────────────────────

function GroupEditForm({ reg, onSave, onCancel }) {
  const [data, setData] = useState({ ...reg });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setData(p => ({ ...p, [k]: v }));
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await api.put(`/api/groups/${reg.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      alert('Group updated and resubmitted for approval!'); onSave();
    } catch (err) { alert('Save failed: ' + (err.response?.data?.error || err.message)); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="edit-form-container">
      <h3>Edit Group Registration</h3>
      <p className="edit-form-hint">Saving changes will resubmit for admin approval.</p>
      <div className="form-group"><label>Group Name <span className="required">*</span></label><input value={data.group_name || ''} onChange={e => set('group_name', e.target.value)} required /></div>
      <div className="form-group"><label>Primary Contact Name</label><input value={data.primary_contact_name || ''} onChange={e => set('primary_contact_name', e.target.value)} /></div>
      <div className="form-group"><label>Primary Contact Email</label><input type="email" value={data.primary_contact_email || ''} onChange={e => set('primary_contact_email', e.target.value)} /></div>
      <h4 className="edit-form-section">Mailing Address</h4>
      <AddressBlock prefix="mailing" data={data} onChange={set} />
      <div className="form-group"><label>Year Established</label><input value={data.year_established || ''} onChange={e => set('year_established', e.target.value)} /></div>
      <h4 className="edit-form-section">Public Location</h4>
      <div className="field-grid-2">
        <div className="form-group"><label>City</label><input value={data.city || ''} onChange={e => set('city', e.target.value)} /></div>
        <div className="form-group"><label>State / Province</label><StateSelect value={data.state} onChange={e => set('state', e.target.value)} /></div>
      </div>
      <div className="form-group"><label>Geographic Description</label><input value={data.geographic_description || ''} onChange={e => set('geographic_description', e.target.value)} /></div>
      <div className="form-group"><label>Group Bio</label><textarea rows="5" value={data.group_bio || ''} onChange={e => set('group_bio', e.target.value)} /></div>
      <div className="form-group"><label>Event Types</label><textarea rows="3" value={data.event_types || ''} onChange={e => set('event_types', e.target.value)} /></div>
      <div className="form-group"><label>Resources</label><textarea rows="3" value={data.resources || ''} onChange={e => set('resources', e.target.value)} /></div>
      <div className="form-group"><label>Accepts New Members</label>
        <select value={data.accepts_new_members || ''} onChange={e => set('accepts_new_members', e.target.value)}>
          <option value="">Select...</option><option value="Yes">Yes</option><option value="No">No</option><option value="Sometimes">Sometimes</option>
        </select>
      </div>
      <div className="form-group"><label>Membership Criteria</label><textarea rows="3" value={data.membership_criteria || ''} onChange={e => set('membership_criteria', e.target.value)} /></div>
      <div className="form-group"><label>Website</label><input value={data.website || ''} onChange={e => set('website', e.target.value)} placeholder="https://" /></div>
      <div className="field-grid-2">
        <div className="form-group"><label>Facebook</label><input value={data.facebook || ''} onChange={e => set('facebook', e.target.value)} /></div>
        <div className="form-group"><label>Instagram</label><input value={data.instagram || ''} onChange={e => set('instagram', e.target.value)} /></div>
        <div className="form-group"><label>TikTok</label><input value={data.tiktok || ''} onChange={e => set('tiktok', e.target.value)} /></div>
        <div className="form-group"><label>YouTube</label><input value={data.youtube || ''} onChange={e => set('youtube', e.target.value)} /></div>
      </div>
      <div className="form-group"><label>Comments</label><textarea rows="3" value={data.comments || ''} onChange={e => set('comments', e.target.value)} /></div>
      <div className="edit-form-actions">
        <button type="submit" disabled={saving} className="btn btn-success">{saving ? 'Saving...' : '💾 Save Changes'}</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}

function IndividualEditForm({ reg, onSave, onCancel }) {
  const [data, setData] = useState({ ...reg });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setData(p => ({ ...p, [k]: v }));
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await api.put(`/api/individuals/${reg.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      alert('Performer updated and resubmitted for approval!'); onSave();
    } catch (err) { alert('Save failed: ' + (err.response?.data?.error || err.message)); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="edit-form-container">
      <h3>Edit Performer Registration</h3>
      <p className="edit-form-hint">Saving changes will resubmit for admin approval.</p>
      <div className="form-group"><label>Display Name <span className="required">*</span></label><input value={data.display_name || ''} onChange={e => set('display_name', e.target.value)} required /></div>
      <div className="form-group"><label>Full Name <span className="text-muted text-sm">(Private)</span></label><input value={data.full_name || ''} onChange={e => set('full_name', e.target.value)} /></div>
      <div className="form-group"><label>Email <span className="text-muted text-sm">(Private)</span></label><input type="email" value={data.email || ''} onChange={e => set('email', e.target.value)} /></div>
      <h4 className="edit-form-section">Mailing Address</h4>
      <AddressBlock prefix="mailing" data={data} onChange={set} />
      <div className="form-group"><label>Year Established</label><input value={data.year_established || ''} onChange={e => set('year_established', e.target.value)} /></div>
      <h4 className="edit-form-section">Public Location</h4>
      <div className="field-grid-2">
        <div className="form-group"><label>City</label><input value={data.city || ''} onChange={e => set('city', e.target.value)} /></div>
        <div className="form-group"><label>State / Province</label><StateSelect value={data.state} onChange={e => set('state', e.target.value)} /></div>
      </div>
      <div className="form-group"><label>Affiliated Group</label><input value={data.affiliated_group || ''} onChange={e => set('affiliated_group', e.target.value)} /></div>
      <div className="form-group"><label>Bio</label><textarea rows="5" value={data.bio || ''} onChange={e => set('bio', e.target.value)} /></div>
      <div className="form-group"><label>Event Types</label><textarea rows="3" value={data.event_types || ''} onChange={e => set('event_types', e.target.value)} /></div>
      <div className="form-group"><label>Website</label><input value={data.website || ''} onChange={e => set('website', e.target.value)} /></div>
      <div className="field-grid-2">
        <div className="form-group"><label>Facebook</label><input value={data.facebook || ''} onChange={e => set('facebook', e.target.value)} /></div>
        <div className="form-group"><label>Instagram</label><input value={data.instagram || ''} onChange={e => set('instagram', e.target.value)} /></div>
        <div className="form-group"><label>TikTok</label><input value={data.tiktok || ''} onChange={e => set('tiktok', e.target.value)} /></div>
      </div>
      <div className="form-group"><label>Comments</label><textarea rows="3" value={data.comments || ''} onChange={e => set('comments', e.target.value)} /></div>
      <div className="edit-form-actions">
        <button type="submit" disabled={saving} className="btn btn-success">{saving ? 'Saving...' : '💾 Save Changes'}</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}

function EventEditForm({ reg, onSave, onCancel }) {
  const [data, setData] = useState({ ...reg });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setData(p => ({ ...p, [k]: v }));
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await api.put(`/api/events/${reg.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      alert('Event updated and resubmitted for approval!'); onSave();
    } catch (err) { alert('Save failed: ' + (err.response?.data?.error || err.message)); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="edit-form-container">
      <h3>Edit Event</h3>
      <p className="edit-form-hint">Saving changes will resubmit for admin approval.</p>
      <div className="form-group"><label>Event Name <span className="required">*</span></label><input value={data.event_name || ''} onChange={e => set('event_name', e.target.value)} required /></div>
      <div className="form-group"><label>Organized By <span className="required">*</span></label><input value={data.organized_by || ''} onChange={e => set('organized_by', e.target.value)} required /></div>
      <div className="form-group"><label>Event Date <span className="required">*</span></label><input type="date" value={data.event_date || ''} onChange={e => set('event_date', e.target.value)} required /></div>
      <div className="form-group"><label>End Date <span className="text-muted text-sm">(Optional — for multi-day events)</span></label><input type="date" value={data.event_end_date || ''} onChange={e => set('event_end_date', e.target.value)} /></div>
      <div className="field-grid-2">
        <div className="form-group"><label>Start Time</label><input type="time" value={data.event_start_time || ''} onChange={e => set('event_start_time', e.target.value)} /></div>
        <div className="form-group"><label>End Time</label><input type="time" value={data.event_end_time || ''} onChange={e => set('event_end_time', e.target.value)} /></div>
      </div>
      <div className="form-group"><label>Location Name <span className="required">*</span></label><input value={data.event_location_name || ''} onChange={e => set('event_location_name', e.target.value)} required /></div>
      <div className="form-group"><label>Location Address</label><input value={data.event_location_address || ''} onChange={e => set('event_location_address', e.target.value)} /></div>
      <div className="field-grid-2">
        <div className="form-group"><label>City <span className="required">*</span></label><input value={data.event_city || ''} onChange={e => set('event_city', e.target.value)} required /></div>
        <div className="form-group"><label>State <span className="required">*</span></label><StateSelect value={data.event_state} onChange={e => set('event_state', e.target.value)} /></div>
      </div>
      <div className="form-group"><label>Website / Social Link</label><input value={data.website_link || ''} onChange={e => set('website_link', e.target.value)} placeholder="https://" /></div>
      <div className="form-group"><label>Event Details <span className="required">*</span></label><textarea rows="6" value={data.event_details || ''} onChange={e => set('event_details', e.target.value)} required /></div>
      <div className="edit-form-actions">
        <button type="submit" disabled={saving} className="btn btn-success">{saving ? 'Saving...' : '💾 Save Changes'}</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}

function CostumeEditForm({ reg, onSave, onCancel }) {
  const [data, setData] = useState({ ...reg });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setData(p => ({ ...p, [k]: v }));
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await api.put(`/api/costumes/${reg.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      alert('Costume maker profile updated and resubmitted for approval!'); onSave();
    } catch (err) { alert('Save failed: ' + (err.response?.data?.error || err.message)); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="edit-form-container">
      <h3>Edit Costume Maker Registration</h3>
      <p className="edit-form-hint">Saving changes will resubmit for admin approval.</p>
      <div className="form-group"><label>Display Name <span className="required">*</span></label><input value={data.display_name || ''} onChange={e => set('display_name', e.target.value)} required /></div>
      <div className="form-group"><label>Full Name</label><input value={data.full_name || ''} onChange={e => set('full_name', e.target.value)} /></div>
      <div className="form-group"><label>Email</label><input type="email" value={data.email || ''} onChange={e => set('email', e.target.value)} /></div>
      <div className="form-group"><label>Phone</label><input value={data.phone || ''} onChange={e => set('phone', e.target.value)} /></div>
      <h4 className="edit-form-section">Location</h4>
      <div className="form-group"><label>Country</label>
        <select value={data.country || 'United States'} onChange={e => set('country', e.target.value)}>
          <option value="United States">United States</option>
          <option value="Canada">Canada</option>
        </select>
      </div>
      <div className="field-grid-2">
        <div className="form-group"><label>City <span className="required">*</span></label><input value={data.city || ''} onChange={e => set('city', e.target.value)} required /></div>
        <div className="form-group"><label>State / Province <span className="required">*</span></label><StateSelect value={data.state} onChange={e => set('state', e.target.value)} /></div>
      </div>
      <h4 className="edit-form-section">About Your Work</h4>
      <div className="form-group"><label>About / What You Do <span className="required">*</span></label><textarea rows="4" value={data.bio || ''} onChange={e => set('bio', e.target.value)} required /></div>
      <div className="form-group"><label>Product Description <span className="required">*</span></label><textarea rows="4" value={data.products_description || ''} onChange={e => set('products_description', e.target.value)} required /></div>
      <div className="form-group"><label>Product Types</label><input value={data.product_types || ''} onChange={e => set('product_types', e.target.value)} placeholder="Masks, Suits, Accessories..." /></div>
      <h4 className="edit-form-section">Purchasing</h4>
      <div className="form-group"><label>How to Purchase <span className="required">*</span></label><textarea rows="3" value={data.how_to_purchase || ''} onChange={e => set('how_to_purchase', e.target.value)} required /></div>
      <div className="form-group"><label>Commission Status</label>
        <select value={data.commission_status || 'Open'} onChange={e => set('commission_status', e.target.value)}>
          <option value="Open">Open</option><option value="Closed">Closed</option><option value="Waitlist">Waitlist</option>
        </select>
      </div>
      <div className="form-group"><label>Price Range</label>
        <select value={data.price_range || ''} onChange={e => set('price_range', e.target.value)}>
          <option value="">— Select —</option><option value="Budget">Budget</option><option value="Mid-range">Mid-range</option><option value="Premium">Premium</option>
        </select>
      </div>
      <div className="form-group"><label>Turnaround Time</label><input value={data.turnaround_time || ''} onChange={e => set('turnaround_time', e.target.value)} /></div>
      <div className="form-group"><label>Ships To</label>
        <select value={data.ships_to || ''} onChange={e => set('ships_to', e.target.value)}>
          <option value="">— Select —</option><option value="United States Only">United States Only</option><option value="Canada Only">Canada Only</option><option value="US & Canada">US &amp; Canada</option><option value="International">International</option>
        </select>
      </div>
      <h4 className="edit-form-section">Links</h4>
      <div className="form-group"><label>Website</label><input value={data.website || ''} onChange={e => set('website', e.target.value)} placeholder="https://" /></div>
      <div className="field-grid-2">
        <div className="form-group"><label>Facebook</label><input value={data.facebook || ''} onChange={e => set('facebook', e.target.value)} /></div>
        <div className="form-group"><label>Instagram</label><input value={data.instagram || ''} onChange={e => set('instagram', e.target.value)} /></div>
        <div className="form-group"><label>TikTok</label><input value={data.tiktok || ''} onChange={e => set('tiktok', e.target.value)} /></div>
        <div className="form-group"><label>YouTube</label><input value={data.youtube || ''} onChange={e => set('youtube', e.target.value)} /></div>
      </div>
      <div className="form-group"><label>Comments</label><textarea rows="3" value={data.comments || ''} onChange={e => set('comments', e.target.value)} /></div>
      <div className="edit-form-actions">
        <button type="submit" disabled={saving} className="btn btn-success">{saving ? 'Saving...' : '💾 Save Changes'}</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}

function ArtistEditForm({ reg, onSave, onCancel }) {
  const [data, setData] = useState({ ...reg });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setData(p => ({ ...p, [k]: v }));
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await api.put(`/api/artists/${reg.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      alert('Artist profile updated and resubmitted for approval!'); onSave();
    } catch (err) { alert('Save failed: ' + (err.response?.data?.error || err.message)); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="edit-form-container">
      <h3>Edit Artist Registration</h3>
      <p className="edit-form-hint">Saving changes will resubmit for admin approval.</p>
      <div className="form-group"><label>Display Name <span className="required">*</span></label><input value={data.display_name || ''} onChange={e => set('display_name', e.target.value)} required /></div>
      <div className="form-group"><label>Full Name</label><input value={data.full_name || ''} onChange={e => set('full_name', e.target.value)} /></div>
      <div className="form-group"><label>Email</label><input type="email" value={data.email || ''} onChange={e => set('email', e.target.value)} /></div>
      <div className="form-group"><label>Phone</label><input value={data.phone || ''} onChange={e => set('phone', e.target.value)} /></div>
      <h4 className="edit-form-section">Location</h4>
      <div className="form-group"><label>Country</label>
        <select value={data.country || 'United States'} onChange={e => set('country', e.target.value)}>
          <option value="United States">United States</option><option value="Canada">Canada</option>
        </select>
      </div>
      <div className="field-grid-2">
        <div className="form-group"><label>City <span className="required">*</span></label><input value={data.city || ''} onChange={e => set('city', e.target.value)} required /></div>
        <div className="form-group"><label>State / Province <span className="required">*</span></label><StateSelect value={data.state} onChange={e => set('state', e.target.value)} /></div>
      </div>
      <h4 className="edit-form-section">About Your Work</h4>
      <div className="form-group"><label>Artist Bio <span className="required">*</span></label><textarea rows="4" value={data.bio || ''} onChange={e => set('bio', e.target.value)} required /></div>
      <div className="form-group"><label>Work Description <span className="required">*</span></label><textarea rows="4" value={data.products_description || ''} onChange={e => set('products_description', e.target.value)} required /></div>
      <div className="form-group"><label>Art Types</label><input value={data.art_types || ''} onChange={e => set('art_types', e.target.value)} placeholder="Paintings, Sculpture, Digital Art..." /></div>
      <div className="form-group"><label>Medium / Materials</label><input value={data.medium || ''} onChange={e => set('medium', e.target.value)} /></div>
      <div className="form-group"><label>Style Description</label><textarea rows="3" value={data.style_description || ''} onChange={e => set('style_description', e.target.value)} /></div>
      <div className="form-group"><label>Ships Physical Work</label>
        <select value={data.ships_physical || 'No'} onChange={e => set('ships_physical', e.target.value)}>
          <option value="No">No</option><option value="Yes">Yes</option>
        </select>
      </div>
      <div className="form-group"><label>Accepts Custom Requests</label>
        <select value={data.accepts_custom_requests || 'No'} onChange={e => set('accepts_custom_requests', e.target.value)}>
          <option value="No">No</option><option value="Yes">Yes</option>
        </select>
      </div>
      <div className="form-group">
        <label className="checkbox-label">
          <input type="checkbox" checked={!!data.uses_ai} onChange={e => set('uses_ai', e.target.checked ? 1 : 0)} />
          My artwork incorporates AI-generated elements
        </label>
      </div>
      <h4 className="edit-form-section">Purchasing</h4>
      <div className="form-group"><label>How to Purchase <span className="required">*</span></label><textarea rows="3" value={data.how_to_purchase || ''} onChange={e => set('how_to_purchase', e.target.value)} required /></div>
      <div className="form-group"><label>Commission Status</label>
        <select value={data.commission_status || 'Open'} onChange={e => set('commission_status', e.target.value)}>
          <option value="Open">Open</option><option value="Closed">Closed</option><option value="Waitlist">Waitlist</option>
        </select>
      </div>
      <div className="form-group"><label>Shop Link <span className="text-muted text-sm">(Etsy, etc.)</span></label><input value={data.shop_link || ''} onChange={e => set('shop_link', e.target.value)} placeholder="https://" /></div>
      <div className="form-group"><label>Price Range</label>
        <select value={data.price_range || ''} onChange={e => set('price_range', e.target.value)}>
          <option value="">— Select —</option><option value="Budget">Budget</option><option value="Mid-range">Mid-range</option><option value="Premium">Premium</option>
        </select>
      </div>
      <div className="form-group"><label>Turnaround Time</label><input value={data.turnaround_time || ''} onChange={e => set('turnaround_time', e.target.value)} /></div>
      <div className="form-group"><label>Ships To</label>
        <select value={data.ships_to || ''} onChange={e => set('ships_to', e.target.value)}>
          <option value="">— Select —</option><option value="United States Only">United States Only</option><option value="Canada Only">Canada Only</option><option value="US & Canada">US &amp; Canada</option><option value="International">International</option>
        </select>
      </div>
      <h4 className="edit-form-section">Links</h4>
      <div className="form-group"><label>Website</label><input value={data.website || ''} onChange={e => set('website', e.target.value)} placeholder="https://" /></div>
      <div className="field-grid-2">
        <div className="form-group"><label>Facebook</label><input value={data.facebook || ''} onChange={e => set('facebook', e.target.value)} /></div>
        <div className="form-group"><label>Instagram</label><input value={data.instagram || ''} onChange={e => set('instagram', e.target.value)} /></div>
        <div className="form-group"><label>TikTok</label><input value={data.tiktok || ''} onChange={e => set('tiktok', e.target.value)} /></div>
        <div className="form-group"><label>YouTube</label><input value={data.youtube || ''} onChange={e => set('youtube', e.target.value)} /></div>
      </div>
      <div className="form-group"><label>Comments</label><textarea rows="3" value={data.comments || ''} onChange={e => set('comments', e.target.value)} /></div>
      <div className="edit-form-actions">
        <button type="submit" disabled={saving} className="btn btn-success">{saving ? 'Saving...' : '💾 Save Changes'}</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}

function PasswordChange() {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) { setMessage({ type: 'danger', text: 'New passwords do not match.' }); return; }
    if (form.newPassword.length < 8) { setMessage({ type: 'danger', text: 'Password must be at least 8 characters.' }); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await api.put('/api/auth/change-password', { currentPassword: form.currentPassword, newPassword: form.newPassword }, { headers: { Authorization: `Bearer ${token}` } });
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) { setMessage({ type: 'danger', text: err.response?.data?.error || 'Failed to change password.' }); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <h2>Change Password</h2>
      {message && <div className={`alert alert-${message.type}`}>{message.text}</div>}
      <form onSubmit={handleSubmit} className="form-narrow">
        <div className="form-group"><label>Current Password</label><input type="password" value={form.currentPassword} onChange={e => setForm(p => ({ ...p, currentPassword: e.target.value }))} required /></div>
        <div className="form-group"><label>New Password <span className="text-muted text-sm">(min 8 characters)</span></label><input type="password" value={form.newPassword} onChange={e => setForm(p => ({ ...p, newPassword: e.target.value }))} required /></div>
        <div className="form-group"><label>Confirm New Password</label><input type="password" value={form.confirmPassword} onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))} required /></div>
        <button type="submit" disabled={saving} className="btn btn-primary">{saving ? 'Saving...' : 'Change Password'}</button>
      </form>
    </div>
  );
}

// ── MAIN DASHBOARD ─────────────────────────────────────────────────────────────

function UserDashboard({ user }) {
  const [groupRegs, setGroupRegs] = useState([]);
  const [individualRegs, setIndividualRegs] = useState([]);
  const [eventRegs, setEventRegs] = useState([]);
  const [costumeRegs, setCostumeRegs] = useState([]);
  const [artistRegs, setArtistRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('groups');
  const [editingId, setEditingId] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [g, i, e, c, a] = await Promise.all([
        api.get('/api/groups/my-registrations', { headers }),
        api.get('/api/individuals/my-registrations', { headers }),
        api.get('/api/events/my-registrations', { headers }).catch(() => ({ data: [] })),
        api.get('/api/costumes/my-registrations', { headers }).catch(() => ({ data: [] })),
        api.get('/api/artists/my-registrations', { headers }).catch(() => ({ data: [] }))
      ]);
      setGroupRegs(g.data);
      setIndividualRegs(i.data);
      setEventRegs(e.data);
      setCostumeRegs(c.data);
      setArtistRegs(a.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Delete this registration? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/${type}/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setNotification({ type: 'success', message: 'Deleted successfully.' });
      fetchAll();
    } catch { setNotification({ type: 'danger', message: 'Delete failed.' }); }
  };

  const handlePurge = async () => {
    if (!window.confirm('WARNING: Delete ALL your registrations and your account permanently?')) return;
    if (!window.confirm('Final confirmation — cannot be undone!')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete('/api/user/purge', { headers: { Authorization: `Bearer ${token}` } });
      localStorage.removeItem('token'); localStorage.removeItem('user');
      window.location.href = '/';
    } catch { setNotification({ type: 'danger', message: 'Purge failed.' }); }
  };

  const statusBadge = (status) => {
    const cls = {
      pending: 'badge badge-pending',
      approved: 'badge badge-approved',
      rejected: 'badge badge-rejected',
      needs_info: 'badge badge-needs-info',
      banned: 'badge'
    }[status] || 'badge';
    return (
      <span className={cls}>{(status || '').replace('_', ' ')}</span>
    );
  };

  const updatePhotos = (regList, setRegList, regId, newPhotos) => {
    setRegList(regList.map(r => r.id === regId ? { ...r, photos: newPhotos } : r));
  };

  const card = (reg, deleteType, regType, EditForm, regList, setRegList, maxPhotos) => (
    <div key={reg.id} className="reg-card">
      <div className="reg-card-header">
        <div className="reg-card-info">
          <h3 className="reg-card-title">
            {reg.group_name || reg.display_name || reg.event_name}
            {statusBadge(reg.status)}
          </h3>
          <p className="reg-card-meta">📍 {reg.city || reg.event_city}, {reg.state || reg.event_state}</p>
          {reg.event_date && <p className="reg-card-meta">📅 {reg.event_date}</p>}
          {reg.commission_status && (
            <p className="reg-card-meta">
              Commissions: <strong className={`commission-status commission-${(reg.commission_status || 'closed').toLowerCase()}`}>{reg.commission_status}</strong>
            </p>
          )}
          {reg.uses_ai && <p className="reg-card-meta text-sm">🤖 Uses AI elements</p>}
          <p className="reg-card-meta text-sm">Submitted: {new Date(reg.created_at).toLocaleDateString()}</p>
          {reg.status === 'needs_info' && reg.admin_notes && (
            <div className="admin-comment-block">
              <div className="admin-comment-title">📋 Admin Comment:</div>
              <p>{reg.admin_notes}</p>
            </div>
          )}
        </div>
        <div className="reg-card-actions">
          <button onClick={() => setEditingId(editingId === reg.id ? null : reg.id)}
            className="btn btn-info btn-sm">
            {editingId === reg.id ? '✕ Close' : '✏️ Edit'}
          </button>
          <button onClick={() => handleDelete(deleteType, reg.id)}
            className="btn btn-danger btn-sm">
            🗑️ Delete
          </button>
        </div>
      </div>

      <PhotoManager
        reg={reg}
        regType={regType}
        maxPhotos={maxPhotos}
        onUpdate={(newPhotos) => updatePhotos(regList, setRegList, reg.id, newPhotos)}
      />

      {editingId === reg.id && (
        <EditForm reg={reg} onSave={() => { setEditingId(null); fetchAll(); }} onCancel={() => setEditingId(null)} />
      )}
    </div>
  );

  if (loading) return <div className="loading">Loading dashboard...</div>;

  const TABS = [
    ['groups',     'Groups',          groupRegs.length],
    ['performers', 'Performers',      individualRegs.length],
    ['costumers',  'Costume Maker',   costumeRegs.length],
    ['artists',    'Artist',          artistRegs.length],
    ['events',     'Events',          eventRegs.length],
    ['password',   'Change Password', null]
  ];

  return (
    <div className="dashboard">
      <NotificationBanner />
      <h1>My Dashboard</h1>

      {notification && (
        <div className={`notification-bar ${notification.type}`}>
          {notification.message}
          <button className="notification-close" onClick={() => setNotification(null)}>✕</button>
        </div>
      )}

      <div className="admin-tabs">
        {TABS.map(([id, label, count]) => (
          <button key={id} className={`admin-tab${activeTab === id ? ' active' : ''}`}
            onClick={() => { setActiveTab(id); setEditingId(null); }}>
            {label}{count !== null ? ` (${count})` : ''}
          </button>
        ))}
      </div>

      {activeTab === 'groups' && (
        <div>
          <div className="tab-section-header">
            <h2>My Group Registrations</h2>
            <a href="/register-group" className="btn btn-gray btn-sm">+ New Group</a>
          </div>
          {groupRegs.length === 0
            ? <p className="text-muted">No groups yet. <a href="/register-group">Register one</a></p>
            : groupRegs.map(r => card(r, 'groups', 'group', GroupEditForm, groupRegs, setGroupRegs, 3))}
        </div>
      )}

      {activeTab === 'performers' && (
        <div>
          <div className="tab-section-header">
            <h2>My Performer Registrations</h2>
            <a href="/register-performer" className="btn btn-gray btn-sm">+ New Performer</a>
          </div>
          {individualRegs.length === 0
            ? <p className="text-muted">No performers yet. <a href="/register-performer">Register one</a></p>
            : individualRegs.map(r => card(r, 'individuals', 'individual', IndividualEditForm, individualRegs, setIndividualRegs, 3))}
        </div>
      )}

      {activeTab === 'costumers' && (
        <div>
          <div className="tab-section-header">
            <h2>My Costume Maker Registrations</h2>
            <a href="/register-costume" className="btn btn-gray btn-sm">+ New Registration</a>
          </div>
          {costumeRegs.length === 0
            ? <p className="text-muted">No costume maker registrations yet. <a href="/register-costume">Register now</a></p>
            : costumeRegs.map(r => card(r, 'costumes', 'costume', CostumeEditForm, costumeRegs, setCostumeRegs, 6))}
        </div>
      )}

      {activeTab === 'artists' && (
        <div>
          <div className="tab-section-header">
            <h2>My Artist Registrations</h2>
            <a href="/register-artist" className="btn btn-gray btn-sm">+ New Registration</a>
          </div>
          {artistRegs.length === 0
            ? <p className="text-muted">No artist registrations yet. <a href="/register-artist">Register now</a></p>
            : artistRegs.map(r => card(r, 'artists', 'artist', ArtistEditForm, artistRegs, setArtistRegs, 10))}
        </div>
      )}

      {activeTab === 'events' && (
        <div>
          <div className="tab-section-header">
            <h2>My Event Submissions</h2>
            <a href="/register-event" className="btn btn-gray btn-sm">+ New Event</a>
          </div>
          {eventRegs.length === 0
            ? <p className="text-muted">No events yet. <a href="/register-event">Submit one</a></p>
            : eventRegs.map(r => card(r, 'events', 'event', EventEditForm, eventRegs, setEventRegs, 3))}
        </div>
      )}

      {activeTab === 'password' && <PasswordChange />}

      <div className="danger-zone">
        <h3>⚠️ Danger Zone</h3>
        <p className="text-muted">Permanently delete all registrations and remove your account. Cannot be undone.</p>
        <button onClick={handlePurge} className="btn btn-danger btn-sm">
          Delete All My Data &amp; Account
        </button>
      </div>
    </div>
  );
}

export default UserDashboard;
